import { Brain, Sparkles, Zap, TreeDeciduous, ArrowRight, MessageSquare, Download } from "lucide-react";
import { useState } from "react";
import { WaitlistDialog } from "./WaitlistDialog";
import WhatsAppPromo from "./WhatsAppPromo";
import { Button } from "./ui/button";

const Hero = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWhatsAppPromoOpen, setIsWhatsAppPromoOpen] = useState(false);

  const handleTagClick = () => {
    window.location.reload();
  };

  return (
    <main className="relative min-h-screen flex flex-col" aria-labelledby="hero-title">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50 -z-10" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 flex-grow">
          <header className="mb-16">
            <h1 id="hero-title" className="flex flex-col items-center text-center gap-4 text-4xl sm:text-5xl md:text-6xl font-bold mb-8 animate-fadeIn">
              <span className="w-full">Welcome to <span className="text-[#0066FF]">DeepSeek AI</span></span>
              <span className="text-2xl sm:text-3xl md:text-4xl text-gray-600 w-full">Advanced Language Models & Enterprise AI Solutions</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto text-center animate-fadeIn" style={{ animationDelay: "0.2s" }}>
              DeepSeek AI delivers cutting-edge artificial intelligence solutions powered by state-of-the-art language models. Transform your business with our enterprise-ready AI technology.
            </p>

            <nav className="flex flex-col sm:flex-row justify-center gap-4 mb-8 animate-fadeIn" style={{ animationDelay: "0.4s" }} aria-label="Primary navigation">
              <a
                href="https://chromewebstore.google.com/detail/deepseek-ai/inhcgfpbfdjbjogdfjbclgolkmhnooop/reviews?hl=en&gl=US"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
                aria-label="Get DeepSeek App"
              >
                Try DeepSeek AI Now
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </a>
            </nav>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button 
                onClick={handleTagClick}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer"
              >
                Advanced Language Models
              </button>
              <button 
                onClick={handleTagClick}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer"
              >
                Enterprise AI Solutions
              </button>
              <button 
                onClick={handleTagClick}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer"
              >
                Real-time Processing
              </button>
            </div>
          </header>
          
          <section className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24 animate-fadeIn" aria-label="Key Features" style={{ animationDelay: "0.6s" }}>
            <article className="p-8 rounded-xl bg-white/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all" aria-labelledby="feature-1-title">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6 mx-auto">
                <Brain className="h-8 w-8 text-deepseek-primary" />
              </div>
              <h2 id="feature-1-title" className="text-2xl font-semibold mb-4">DeepSeek AI Learning</h2>
              <p className="text-gray-600">Experience our advanced neural networks and machine learning models that adapt to your enterprise needs. Built for scalability and performance.</p>
            </article>
            <article className="p-8 rounded-xl bg-white/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all" aria-labelledby="feature-2-title">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6 mx-auto">
                <Sparkles className="h-8 w-8 text-deepseek-primary" />
              </div>
              <h2 id="feature-2-title" className="text-2xl font-semibold mb-4">Language Intelligence</h2>
              <p className="text-gray-600">DeepSeek AI's state-of-the-art language models deliver unmatched text understanding and generation capabilities for your applications.</p>
            </article>
            <article className="p-8 rounded-xl bg-white/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all" aria-labelledby="feature-3-title">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6 mx-auto">
                <Zap className="h-8 w-8 text-deepseek-primary" />
              </div>
              <h2 id="feature-3-title" className="text-2xl font-semibold mb-4">Enterprise AI Solutions</h2>
              <p className="text-gray-600">Transform your business with real-time AI processing and intelligent automation. Custom solutions designed for enterprise needs.</p>
            </article>
        </section>
          
        <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 mb-8 max-w-3xl mx-auto shadow-sm border border-blue-100">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">What is DeepSeek AI?</h2>
          <p className="text-lg text-gray-700">
            <strong>DeepSeek AI</strong> is an AI research company focused on cost-efficient, high-performance language models. It provides an open-source alternative to GPT-4, making AI technology more accessible to developers and businesses.
          </p>
        </div>

        <section className="mt-24 py-16 bg-white/80 rounded-3xl shadow-lg">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-3">How does DeepSeek AI compare to GPT-4?</h3>
                <p className="text-gray-700">DeepSeek AI offers high-performance models at a lower cost than OpenAI's GPT-4, making it an attractive alternative for businesses seeking scalable AI solutions.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Is DeepSeek AI open-source?</h3>
                <p className="text-gray-700">Yes, DeepSeek AI provides open-source models, allowing developers to explore and implement advanced AI capabilities without high licensing fees.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">What makes DeepSeek AI different?</h3>
                <p className="text-gray-700">DeepSeek AI stands out with its focus on efficiency, performance, and accessibility. Our models are designed to be cost-effective while delivering state-of-the-art results for a variety of AI applications.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-24 py-16 bg-white/80 rounded-3xl shadow-lg">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-8">Why Choose DeepSeek AI?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <TreeDeciduous className="h-5 w-5 text-green-500" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Advanced AI Technology</h3>
                  <p className="text-gray-600">State-of-the-art language models and neural networks powering next-generation applications.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-deepseek-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Enterprise-Ready</h3>
                  <p className="text-gray-600">Scalable solutions designed for business needs with robust security and performance.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="py-16 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Experience DeepSeek AI?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Get our browser extension and start using DeepSeek AI's powerful capabilities today.
          </p>
          <a
            href="https://chromewebstore.google.com/detail/deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
            aria-label="Get the DeepSeek AI App"
          >
            Get the DeepSeek AI App
            <Download className="ml-2 h-5 w-5" aria-hidden="true" />
          </a>
        </div>
      </section>

      <WaitlistDialog 
        isOpen={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)} 
      />
      
      <WhatsAppPromo
        isOpen={isWhatsAppPromoOpen}
        onClose={() => setIsWhatsAppPromoOpen(false)}
      />
    </main>
  );
};

export default Hero;
